import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
  Typography,
  IconButton,
  Avatar,
  InputAdornment,
  Divider,
  Card,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Stars as StarsIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday as CalendarTodayIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import { updateFeatureToTenant } from "../../../../Services/BrokersApi";
import { themeColors } from "../../../../theme/colors";

const EditFeatureDialog = ({ open, tenantId, activeFeatures = [], feature = null, onClose, onUpdated }) => {
  const [selectedFeatureId, setSelectedFeatureId] = useState(null);
  const [value, setValue] = useState("");
  const [monthlyCost, setMonthlyCost] = useState("");
  const [yearlyCost, setYearlyCost] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // دمج الميزة الممرّرة مع قائمة الميزات الفعّالة لضمان ظهور اسمها في قائمة الاختيار
  const mergedActive = React.useMemo(() => {
    const list = Array.isArray(activeFeatures) ? [...activeFeatures] : [];
    const featKey = String(feature?.id ?? feature?.pivot?.feature_id ?? "");
    if (feature && featKey) {
      const exists = list.some(
        (f) => String(f?.id ?? f?.pivot?.feature_id ?? "") === featKey
      );
      if (!exists) list.unshift(feature);
    }
    return list;
  }, [activeFeatures, feature]);

  // مساعدة للعثور على كائن الميزة داخل activeFeatures
  const findFeatureById = (id) => {
    if (!id) return null;
    return mergedActive.find(
      (f) =>
        String(f?.id ?? "") === String(id) || String(f?.pivot?.feature_id ?? "") === String(id)
    );
  };

  // قيمة الميزة المحددة كمصدر بيانات للعرض/التعبئة
  const selectedFeature = useMemo(() => findFeatureById(selectedFeatureId), [selectedFeatureId, activeFeatures]);

  // عند فتح الحوار أو تغيّر feature/activeFeatures، عبّئ القيم الأولية
  useEffect(() => {
    if (!open) return;
    // استخدم الميزة الممررة أو أول عنصر من القائمة المدمجة
    const initialFeature = feature ?? (mergedActive.length ? mergedActive[0] : null);
    if (initialFeature) {
      const id = initialFeature.id ?? initialFeature.pivot?.feature_id ?? null;
      setSelectedFeatureId(id);
      const pivot = initialFeature.pivot || {};
      setValue(pivot.value ?? "");
      setMonthlyCost(pivot.monthly_cost ?? "");
      setYearlyCost(pivot.yearly_cost ?? "");
      setStartDate(pivot.start_date ? pivot.start_date.split("T")[0] : pivot.start_date ?? "");
      setEndDate(pivot.end_date ? pivot.end_date.split("T")[0] : pivot.end_date ?? "");
    } else {
      setSelectedFeatureId(null);
      setValue("");
      setMonthlyCost("");
      setYearlyCost("");
      setStartDate("");
      setEndDate("");
    }
    setError(null);
  }, [open, feature, /* keep activeFeatures out, use mergedActive dependency next */ mergedActive]);

  // عندما يغيّر المستخدم الاختيار، اعثر على الميزة وعبّئ الحقول تلقائياً
  // useEffect(() => {
  //   if (!selectedFeature) return;
  //   const pivot = selectedFeature.pivot || {};
  //   setValue(pivot.value ?? "");
  //   setMonthlyCost(pivot.monthly_cost ?? "");
  //   setYearlyCost(pivot.yearly_cost ?? "");
  //   setStartDate(pivot.start_date ? pivot.start_date.split("T")[0] : pivot.start_date ?? "");
  //   setEndDate(pivot.end_date ? pivot.end_date.split("T")[0] : pivot.end_date ?? "");
  // }, [selectedFeature]);

  const handleSave = async () => {
    if (!tenantId || !selectedFeatureId) {
      setError("tenantId أو feature غير محدد");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("authToken");
      const payload = {};
      if (value !== "") payload.value = value;
      if (monthlyCost !== "") payload.monthly_cost = monthlyCost;
      if (yearlyCost !== "") payload.yearly_cost = yearlyCost;
      if (startDate !== "") payload.start_date = startDate;
      if (endDate !== "") payload.end_date = endDate;

      await updateFeatureToTenant(tenantId, selectedFeatureId, payload, token);
      if (onUpdated) onUpdated();
    } catch (err) {
      setError((err && (err.message || err.msg || JSON.stringify(err))) || "حدث خطأ");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          pb: 2,
          background: `linear-gradient(135deg, ${themeColors.primary.main} 0%, ${themeColors.primary.dark} 100%)`,
          color: "white",
          position: "relative",
        }}
      >
        <Avatar
          sx={{
            width: 48,
            height: 48,
            bgcolor: "rgba(255,255,255,0.25)",
            backdropFilter: "blur(10px)",
          }}
        >
          <EditIcon />
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            تعديل الميزة
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            تعديل بيانات الميزة المفعلة
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            left: 8,
            top: 8,
            color: "white",
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.15)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(220, 53, 69, 0.15)",
            }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Available Features Info */}
        {Array.isArray(activeFeatures) && activeFeatures.length > 0 && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: `${themeColors.primary.main}08`,
              borderRadius: 2,
              border: `1px solid ${themeColors.primary.main}30`,
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <StarsIcon sx={{ color: themeColors.primary.main, fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                الميزات المتاحة للتعديل
              </Typography>
              <Chip
                label={activeFeatures.length}
                size="small"
                sx={{
                  bgcolor: themeColors.primary.main,
                  color: "white",
                  fontWeight: 600,
                }}
              />
            </Box>
          </Box>
        )}

        <Card
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: `1px solid ${themeColors.border.light}`,
            bgcolor: themeColors.background.default,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, mb: 2.5, color: themeColors.text.primary }}
          >
            📝 بيانات الميزة
          </Typography>

          <Box display="flex" flexDirection="column" gap={2.5}>
            <TextField
              select
              label="اختر الميزة للتعديل"
              value={selectedFeatureId ?? ""}
              onChange={(e) => setSelectedFeatureId(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CategoryIcon sx={{ color: themeColors.text.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover fieldset": {
                    borderColor: themeColors.primary.main,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: themeColors.primary.main,
                  },
                },
              }}
            >
              {mergedActive.map((f) => (
                <MenuItem key={f.id ?? f.pivot?.feature_id} value={f.id ?? f.pivot?.feature_id}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <StarsIcon sx={{ fontSize: 16, color: themeColors.primary.main }} />
                    {f.name ?? f.title ?? `feature-${f.id ?? f.pivot?.feature_id}`}
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="القيمة"
              placeholder="مثال: 1"
              value={value ?? ""}
              onChange={(e) => setValue(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <StarsIcon sx={{ color: themeColors.text.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover fieldset": {
                    borderColor: themeColors.primary.main,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: themeColors.primary.main,
                  },
                },
              }}
            />

            <Box display="flex" gap={2}>
              <TextField
                label="التكلفة الشهرية"
                placeholder="0.00"
                value={monthlyCost ?? ""}
                onChange={(e) => setMonthlyCost(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon sx={{ color: themeColors.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: themeColors.primary.main,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themeColors.primary.main,
                    },
                  },
                }}
              />

              <TextField
                label="التكلفة السنوية"
                placeholder="0.00"
                value={yearlyCost ?? ""}
                onChange={(e) => setYearlyCost(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon sx={{ color: themeColors.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: themeColors.primary.main,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themeColors.primary.main,
                    },
                  },
                }}
              />
            </Box>

            <Box display="flex" gap={2}>
              <TextField
                label="تاريخ البداية"
                type="date"
                value={startDate ?? ""}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon sx={{ color: themeColors.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: themeColors.primary.main,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themeColors.primary.main,
                    },
                  },
                }}
              />

              <TextField
                label="تاريخ النهاية"
                type="date"
                value={endDate ?? ""}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon sx={{ color: themeColors.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: themeColors.primary.main,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themeColors.primary.main,
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </Card>

        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: `${themeColors.warning}10`,
            borderRadius: 2,
            border: `1px solid ${themeColors.warning}30`,
          }}
        >
          <Typography variant="caption" sx={{ color: themeColors.text.secondary }}>
            ⚠️ <strong>تنبيه:</strong> سيتم تحديث بيانات الميزة المحددة فقط. الحقول الفارغة لن تتأثر.
          </Typography>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 3,
            borderColor: themeColors.border.main,
            color: themeColors.text.secondary,
            "&:hover": {
              borderColor: themeColors.text.primary,
              bgcolor: "transparent",
            },
          }}
        >
          إلغاء
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || !selectedFeatureId}
          sx={{
            borderRadius: 2,
            px: 4,
            bgcolor: themeColors.primary.main,
            boxShadow: `0 4px 12px ${themeColors.primary.main}40`,
            "&:hover": {
              bgcolor: themeColors.primary.dark,
              boxShadow: `0 6px 16px ${themeColors.primary.main}60`,
            },
            "&:disabled": {
              bgcolor: themeColors.text.light,
            },
          }}
        >
          {loading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} color="inherit" />
              <span>جاري الحفظ...</span>
            </Box>
          ) : (
            <>
              <EditIcon sx={{ mr: 1, fontSize: 20 }} />
              حفظ التعديلات
            </>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditFeatureDialog;
